import React, { useMemo, useCallback, useRef } from 'react';
import styles from './styles.module.css';
import clsx from 'clsx';
import { Formik, FormikValues, FormikHelpers } from 'formik';
import { model, validationSchema, initialValues } from './config';

import { InputField } from 'common';
import { Button } from 'common/new-button';
import { Routes } from 'constants/routes';
import { Icon } from 'common/icon';
import { IComment, IComplaint } from 'constants/types';

import { useAlert } from 'components';
import { useRouter } from 'next/router';
import { useCreatePublicationCommentsMutation } from 'hooks/api/publication-comments';
import { useCreateCasinoComplaintsMutation } from 'hooks/api/complaints';
import { useCreateQuestionMutation } from 'hooks/api/questions';
import { useMeQuery } from 'hooks/api/users';

import { handleMutation } from 'utils/handleMutation';
import { shortenFileName } from 'utils/file';

function getReplyAuthor(reply: IComplaint | IComment) {
  return (
    reply.user?.username ??
    (reply as { user_id?: { username?: string } }).user_id?.username ??
    'User'
  );
}

export interface Props {
  variant: 'complaint' | 'question' | 'publication';
  casinoId?: string;
  parent?: string;
  reply?: IComplaint | IComment;
  onCommentAdded?: () => void;
  onCancel?: () => void;
  anchor?: string;
  position?: 'block' | 'fixed';
}

export const WriteComment = ({
  variant,
  casinoId,
  parent,
  reply,
  onCommentAdded,
  onCancel,
  anchor,
  position,
}: Props) => {
  const alert = useAlert();
  const router = useRouter();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const replyRef = useRef(reply);

  replyRef.current = reply;

  const { data: me } = useMeQuery();
  const [placeComment, commentMutation] =
    useCreatePublicationCommentsMutation();
  const [placeComplaint, complaintMutation] =
    useCreateCasinoComplaintsMutation();
  const [placeQuestion, questionMutation] = useCreateQuestionMutation();

  const isLoading = useMemo(() => {
    switch (variant) {
      case 'complaint':
        return complaintMutation.isLoading;
      case 'question':
        return questionMutation.isLoading;
      case 'publication':
        return commentMutation.isLoading;
      default:
        return false;
    }
  }, [
    questionMutation?.isLoading,
    complaintMutation?.isLoading,
    commentMutation.isLoading,
    variant,
  ]);

  const dynamicStylePosition = useMemo(() => {
    switch (position) {
      case 'fixed':
        return styles.wrapper_fixed;
      default:
        return null;
    }
  }, [position]);

  const onSuccess = useCallback(() => {}, []);

  handleMutation({ ...commentMutation, onSuccess });
  handleMutation({ ...complaintMutation, onSuccess });
  handleMutation({ ...questionMutation, onSuccess });

  const onSubmit = async (
    values: FormikValues,
    { resetForm }: FormikHelpers<FormikValues>
  ) => {
    const { message, subject, screenshot } = values;
    if (!message) {
      alert.error('Please fill message');
      return;
    }

    if (!me?.user?._id) {
      router.push(Routes.login);
      return;
    }

    if (variant === 'complaint' && casinoId && parent) {
      await placeComplaint({
        parent,
        replyId: replyRef.current?._id,
        casinoId,
        message: message,
        title: subject,
        image: screenshot,
      });

      alert.success('Complaint successfully added');
      resetForm();
      if (onCommentAdded) onCommentAdded();
      return;
    }

    if (variant === 'question') {
      await placeQuestion({
        replyId: replyRef.current?._id,
        message: message,
        title: subject,
        parent: parent ?? null,
        image: screenshot,
      });

      alert.success('Question successfully added');
      resetForm();
      if (onCommentAdded) onCommentAdded();
      return;
    }
    if (variant === 'publication' && parent) {
      await placeComment({
        publication_id: parent,
        message,
        likes: 0,
        dislikes: 0,
        parent: null,
      });

      alert.success('Comment successfully added');
      if (onCommentAdded) onCommentAdded();
      resetForm();
    }
  };

  if (variant)
    return (
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ dirty, isSubmitting, submitForm, values }) => {
          return (
            <div
              className={clsx(
                styles.wrapper_comment_publication,
                dynamicStylePosition
              )}
              ref={modalRef}
              id={anchor}
            >
              <div className={styles.top}>
                <h2 className={styles.title_comment_publication}>
                  <div className={styles.messageIcon}>
                    <Icon.CommentSecond />
                  </div>
                  {reply ? (
                    <div className={styles.writeReplyToContainer}>
                      <span className={styles.writeReplyTo}>
                        Write a reply to
                      </span>
                      <span className={styles.replyUsername}>
                        {getReplyAuthor(reply)}
                      </span>
                      ...
                    </div>
                  ) : (
                    'Write a comment...'
                  )}
                </h2>
              </div>
              {reply && (
                <div className={styles.replyBlockContainer}>
                  <div className={styles.replyBlockLeftBorder} />
                  <div className={styles.replyBlock_question}>
                    <div className={styles.replyText}>
                      <span className={styles.replyMessage_question}>
                        {reply.message}
                      </span>
                    </div>
                    <div
                      className={clsx(
                        styles.closeButton,
                        styles.closeButton_background
                      )}
                    >
                      <button onClick={onCancel}>
                        <Icon.CloseSecond
                          width={20}
                          height={20}
                          fill="white"
                          strokeWidth={1}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.textInput_comment_publication}>
                <InputField {...model.message} />
                <div
                  className={clsx(
                    styles.actions_background,
                    styles.actions_background_color_black
                  )}
                >
                  <div className={styles.screenshotInput}>
                    <div className={styles.image}>
                      <Icon.Image
                        width={16}
                        height={16}
                        color="white"
                        fillOpacity={0.4}
                      />

                      <InputField
                        {...model.screenshot}
                        label={
                          shortenFileName((values?.screenshot as any)?.name) ??
                          'Attach image'
                        }
                      />
                    </div>
                    <div className={styles.actions_comment_publication}>
                      <div className={styles.submitButton_comment_publication}>
                        <Button
                          icon={<Icon.Send />}
                          text={isLoading ? 'Loading...' : 'Send'}
                          onClick={submitForm}
                          disabled={!dirty || isSubmitting}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </Formik>
    );

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ dirty, isSubmitting, submitForm, values }) => {
        return (
          <div
            className={clsx(
              dynamicStylePosition,
              (variant === 'complaint' || variant === 'publication') &&
                styles.wrapper_huge_size
            )}
            ref={modalRef}
            id={anchor}
          >
            <div className={styles.top}>
              <h2 className={styles.title}>{'Write comment'}</h2>
            </div>
            {reply && (
              <div className={styles.replyBlock}>
                <div className={styles.replyText}>
                  <p className={styles.replyUsername}>
                    {'Reply to'} {getReplyAuthor(reply)}
                  </p>
                  <div className={styles.replyMessage}>{reply.message}</div>
                </div>
                <div className={styles.closeButton}>
                  <button onClick={onCancel}>
                    <Icon.CloseSecond
                      width={20}
                      height={20}
                      fill="#b0b1b5"
                      strokeWidth={1}
                    />
                  </button>
                </div>
              </div>
            )}

            <div className={styles.textInput}>
              <InputField {...model.message} />
              <div className={styles.actions_background}>
                <div
                  className={clsx(
                    styles.actions,
                    variant === 'publication' && styles.actions_end
                  )}
                >
                  {variant !== 'publication' && (
                    <div className={styles.screenshotInput}>
                      <InputField {...model.screenshot} />
                      <div className={styles.wrapperScreenshotButton}>
                        <Icon.Photo width="20px" />
                        <span>
                          {shortenFileName((values?.screenshot as any)?.name)}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className={styles.submitButton}>
                    <Button
                      text={isLoading ? 'Loading...' : 'Send'}
                      onClick={submitForm}
                      disabled={!dirty || isSubmitting}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};
